import React, { useState, useEffect } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  add,
  isToday,
  isSameMonth,
  startOfDay,
} from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, MapPinIcon, UserCircleIcon, XCircleIcon, DocumentIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Meetings({ selectedDateProp }) {

  const Navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(selectedDateProp || new Date());
  const [meetings, setMeetings] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [report, setReport] = useState(null);

  console.log(selectedDateProp);

  // Generate the days of the calendar dynamically
  const currentDate = new Date();
  const startDate = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
  const endDate = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });

  const days = [];
  let day = startOfDay(startDate); // Normalize the start day to midnight

  // Populate the days array with all days of the current month
  while (day <= endDate) {
    days.push(day);
    day = add(day, { days: 1 }); // Move to the next day
  }

  const token = localStorage.getItem('token');

  // Fetch meetings data for a selected day
  const fetchMeetings = async (date) => {
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      console.log(formattedDate);
      const response = await fetch(`https://api.insightos.co.uk/api/meetings/${formattedDate}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json',

         },
         credentials : 'include',

      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setMeetings(data.data);
      setUpdated(null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchMeetings(selectedDate); // Fetch meetings on initial render
  }, [selectedDate]);

  // Navigate to previous or next month
  const previousMonth = () => setCurrentMonth(add(currentMonth, { months: -1 }));
  const nextMonth = () => setCurrentMonth(add(currentMonth, { months: 1 }));

  // Handle the Delete button click
  const handleDelete = async (id) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/deletemeeting/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(data);
      fetchMeetings(selectedDate);
      setUpdated("Meeting Deleted");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="space-y-8 ml-3">
      <h2 className="text-base font-semibold text-gray-900">Meeting Schedule</h2>

      {report && (
        <div className="rounded-md bg-blue-100 p-4 text-center text-blue-700">
          <button onClick={() => setReport(null)} className="text-red-700 hover:underline">
            <span className="sr-only">Close</span>
            <XCircleIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <h3 className="font-semibold">Meeting Report</h3>
          <p className="text-sm">{report}</p>
        </div>
      )}

      {updated && <div className="text-green-600">{updated}</div>}

      {/* Calendar Section */}
      <div className="text-center">
        <div className="flex items-center text-gray-900 justify-center space-x-2">
          <button
            type="button"
            onClick={previousMonth}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex-auto text-sm font-semibold">{format(currentMonth, 'MMMM yyyy')}</div>
          <button
            type="button"
            onClick={nextMonth}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-7 text-xs text-gray-500">
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
          <div>S</div>
        </div>

        <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
          {days.map((day, dayIdx) => (
            <button
              key={day.toISOString()}
              onClick={() => {
                setSelectedDate(day);
                fetchMeetings(day);
              }}
              type="button"
              className={classNames(
                'py-1.5 hover:bg-green-500 focus:z-10',
                isSameMonth(day, currentMonth) ? 'bg-green-300' : 'bg-purple-100',
                isToday(day) && 'text-indigo-600 font-bold',
                selectedDate && day.toDateString() === selectedDate.toDateString() && 'text-black animate-pulse',
                dayIdx === 0 && 'rounded-tl-lg',
                dayIdx === 6 && 'rounded-tr-lg',
                dayIdx === days.length - 7 && 'rounded-bl-lg',
                dayIdx === days.length - 1 && 'rounded-br-lg'
              )}
            >
              <time dateTime={day.toISOString()} className="mx-auto flex items-center justify-center rounded-full">
                {format(day, 'd')}
              </time>
            </button>
          ))}
        </div>
      </div>

      {/* Meetings Section */}
      <ol className="mt-6 text-sm">
        {meetings.length > 0 ? (
          meetings.map((meeting, idx) => {
            let meetingBackgroundColor = "bg-blue-100"; // Default color
            let statusText = "Not Started";
            let showCancelButton = true;

            if (meeting.Start === 1 && meeting.Finished === 1) {
              meetingBackgroundColor = "bg-red-100"; // Red background for completed meeting
              statusText = "Completed";
              showCancelButton = false;
            } else if (meeting.Start === 1 && meeting.Finished === 0) {
              meetingBackgroundColor = "bg-green-100"; // Green background for in-progress meeting
              statusText = "In Progress";
            }

            return (
              <li key={idx} className={`relative mt-5 rounded-lg border ${meetingBackgroundColor} p-4 shadow-md`}>
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <h3 className="font-bold text-lg text-blue-700">{meeting.Name}</h3>
                  </div>
                  <div className="col-span-1">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="h-5 w-5 text-blue-700" aria-hidden="true" />
                      <span className="text-blue-700 font-semibold">{meeting.Time}</span>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="h-5 w-5 text-black" aria-hidden="true" />
                      <span className="text-blue-700 font-semibold">{meeting.Location}</span>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="flex items-center space-x-2">
                      <UserCircleIcon className="h-5 w-5 text-black" aria-hidden="true" />
                      <span className="text-blue-700 font-semibold">{meeting.Attendees}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-2">
                  <span className="block text-gray-600 font-medium">Notes:</span>
                  <p className="text-blue-700">{meeting.Notes}</p>
                </div>

                <div className="mt-2 text-center text-gray-600">
                  <span className={`block ${meeting.Start === 1 ? "font-bold" : "font-normal"} text-sm`}>
                    {statusText}
                  </span>
                </div>
                <button
                  onClick={() => {
                    const message = meeting;
                    Navigate('/meeting', { state: { meeting: message } });
                  }}
                  data-component="meeting-lite-actions"
                  data-time-saved="2"
                  data-feature="open_meeting_details_get"
                  className="flex w-full items-center justify-center rounded-none rounded-bl-lg border border-transparent px-4 py-3 text-sm font-medium text-blue-700 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Accept
                </button>

                <div className="mt-4 flex justify-end space-x-3">
                  {showCancelButton && (
                    <button
                      onClick={() => handleDelete(meeting.id)}
                      type="button"
                      data-component="meeting-lite-actions"
                      data-time-saved="2"
                      data-feature="cancel_meeting_delete"
                      className="inline-flex items-center gap-x-1.5 rounded-md bg-red-700 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-600"
                    >
                      Cancel Meeting
                      <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  )}

                  {meeting.Start === 1 && meeting.Finished === 1 && (
                    <button
                      onClick={() => setReport(meeting.MeetingNotes)}
                      data-component="meeting-lite-actions"
                      data-time-saved="2"
                      data-feature="view_meeting_report_get"
                      className="inline-flex items-center gap-x-1.5 rounded-md bg-red-100 px-2.5 py-1.5 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-200"
                    >
                      Meeting Report
                      <DocumentIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </li>
            );
          })
        ) : (
          <div className="rounded-md bg-gray-100 p-4 text-center text-gray-700">
            No Meetings for this day.
          </div>
        )}
      </ol>
    </div>
  );
}
